import { Box, Button, CircularProgress, MenuItem, Stack } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { RentalTrailer } from "src/models";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import { Constants } from "src/constants";
import TruxSelectField from "src/components/inputs/trux-select-field";

type RentalTrailerModalProps = {
  selectedRentalTrailerModel: ModelSavingData<RentalTrailer>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateRentalTrailer: VoidFunction,
}

export function RentalTrailerModal(
  { selectedRentalTrailerModel, isOpen, setIsOpen, updateRentalTrailer }: RentalTrailerModalProps
) {


  const [loadingVin, setLoadingVin] = useState(false);

  const decodeVin = () => {
    setLoadingVin(true);
    const rentalTrailer = selectedRentalTrailerModel.model;
    if (rentalTrailer) {
      if (!rentalTrailer.vin) {
        enqueueSnackbar("Please enter a vin", { variant: "error" });
        return;
      }

      fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${encodeURIComponent(rentalTrailer.vin)}?format=json`).then(response => response.json()).then((response) => {
        let make = "";
        let model = "";

        response.Results.forEach((result: { Variable: string; Value: string }) => {
          if (result.Variable === "Make") {
            make = result.Value || "";
          } else if (result.Variable === "Model") {
            model = result.Value || "";
          }
        });
        if (make || model) {
          selectedRentalTrailerModel.update(() => {
            rentalTrailer.make = make;
            rentalTrailer.model = model;
          })
        } else {
          enqueueSnackbar("Make, Model and/or Year could not be decoded", { variant: "error" });
        }
      }).finally(() => {
        setLoadingVin(false);
      })
    }
  }


  return selectedRentalTrailerModel.model && <SimpleDialog
    title={<>{selectedRentalTrailerModel.isNew ? "New Trailer" : "Update Trailer"}</>}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={updateRentalTrailer}
    submitDisabled={selectedRentalTrailerModel.loading}
    submitButton={selectedRentalTrailerModel.isNew ? "New Trailer" : "Update Trailer"}
    submitButtonIcon={selectedRentalTrailerModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
    submitButtonColor={selectedRentalTrailerModel.isNew ? "success" : "info"}>


    <DisplayErrors savingData={selectedRentalTrailerModel} />

    <Stack direction="column" gap={1}>

    <TruxFormField label="Vehicle Number *">
        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="trailerUnitNumber" />
      </TruxFormField>

      <TruxFormField label="Vin *">
        <Box display="flex" gap={2}>
          <Box flex={1}>
            <TruxTextField modelSavingData={selectedRentalTrailerModel} field="vin" />
          </Box>
          <Box>
            <Button variant="soft" color="info" onClick={decodeVin}>
              {loadingVin && <CircularProgress size={16} sx={{ marginRight: "8px" }} />} Decode
            </Button>
          </Box>
        </Box>
      </TruxFormField>

      <TruxFormField label="Make">
        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="make" />
      </TruxFormField>

      <TruxFormField label="Model">
        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="model" />
      </TruxFormField>

      <TruxFormField label="Year">
        <TruxTextField modelSavingData={selectedRentalTrailerModel} field="year" />
      </TruxFormField>

      <TruxFormField label="License Plate *">
        <Box display="flex" gap={1}>
          <Box>
            <TruxTextField modelSavingData={selectedRentalTrailerModel} field="licensePlate" />
          </Box>

          <Box>
            <TruxSelectField modelSavingData={selectedRentalTrailerModel} field="licensePlateState" >
              <MenuItem value="" selected={!!selectedRentalTrailerModel.model?.licensePlateState}>Please Select</MenuItem>
              {Constants.STATES.map((state) => <MenuItem value={state} key={state} selected={state === selectedRentalTrailerModel.model?.licensePlateState}>{state}</MenuItem>)}
            </TruxSelectField>
          </Box>
        </Box>
      </TruxFormField>

      <TruxFormField label="Active">
        <TruxCheckboxField modelSavingData={selectedRentalTrailerModel} field="active" />
      </TruxFormField>
    </Stack>
  </SimpleDialog >
}
